import { confirmDial } from '@/features/cv/components/ConfirmDialog';
import {
  CvValidType,
  ValidLangId,
} from '@/lib/utils/calculatePercentCv';
import { EsCvData, LangType } from '@/shared/cv/cv.es.dto';
import { LocalizedNameSource } from '@/types/localizedName';
import { IOptions } from '@/shared/options/options.model';

export function isMnLanguage(langId: number): boolean {
  return langId === 1;
}

export function getLocalizedName(
  item: LocalizedNameSource | null | undefined,
  langId: number,
  fallback = ''
): string {
  if (!item) return fallback;

  const nameMn = (
    item.name_mn ??
    item.nameMn ??
    (item.mn != null ? String(item.mn) : undefined) ??
    item.name ??
    ''
  ).trim();

  const nameEn = (
    item.name_en ??
    item.nameEn ??
    (item.en != null ? String(item.en) : undefined) ??
    ''
  ).trim();

  if (isMnLanguage(langId)) {
    return nameMn || nameEn || fallback;
  }

  return nameEn || nameMn || fallback;
}

export function isCvAnket(cvPercent?: EsCvData['cvPercent'] | null): boolean {
  return (cvPercent?.mn ?? 0) > 0 || (cvPercent?.en ?? 0) > 0;
}

export function getCvPercent(
  cvPercent: EsCvData['cvPercent'] | null | undefined,
  currentLanguage: number
): number {
  return (
    (currentLanguage === 1 ? cvPercent?.mn : cvPercent?.en) ?? 0
  );
}

function getCvLangText(
  value: LangType | undefined | null,
  langId: ValidLangId
): string {
  if (!value) {
    return '';
  }

  const text = langId === 1 ? value.mn : value.en;
  return String(text ?? '').trim();
}

function countSkillsByGroup(cvDetail: EsCvData, groupId: number): number {
  return (cvDetail.skills ?? []).filter((skill) => skill.skillGroup === groupId)
    .length;
}

export function filterCvItemsByLang<T extends { langId: number }>(
  items: T[] | null | undefined,
  langId: number
): T[] {
  return (items ?? []).filter((item) => item.langId === langId);
}

export function buildCvValidDataFromDetail(
  cvDetail: EsCvData,
  langId: ValidLangId,
  fallbackAvatar?: string
): CvValidType {
  const about = getCvLangText(cvDetail.about, langId);
  const education = filterCvItemsByLang(cvDetail.education, langId);
  const experience = filterCvItemsByLang(cvDetail.experience, langId);
  const training = filterCvItemsByLang(cvDetail.training, langId);

  return {
    picture: (cvDetail.picture ?? fallbackAvatar ?? '').length > 0,
    about: about.length > 0,
    lived_in: (cvDetail.livedIn ?? 0) > 0,
    wanted_salary: (cvDetail.salary?.wantedSalary ?? 0) > 0,
    education: education.length > 0,
    experience: experience.length > 0,
    target_positions: (cvDetail.targetPositions ?? []).length > 0,
    skill_lang: countSkillsByGroup(cvDetail, 2) > 0,
    skill_com: countSkillsByGroup(cvDetail, 3),
    skill_personal: countSkillsByGroup(cvDetail, 1),
    skill_professional: countSkillsByGroup(cvDetail, 6),
    courses_certificates: training.length > 0,
    firstname: getCvLangText(cvDetail.firstname, langId).length > 0,
    lastname: getCvLangText(cvDetail.lastname, langId).length > 0,
    gender: (cvDetail.gender ?? 0) > 0,
    birthdate: (cvDetail.birthdate ?? '').toString().length > 0,
    phone: (cvDetail.phone1 ?? 0) > 0,
    email: (cvDetail.email ?? '').trim().length > 0,
  };
}

export function getCvOptionLabel(
  option: IOptions | undefined,
  langId: number
): string {
  return getLocalizedName(option, langId);
}

export function showCvNotCreatedDialog(langId: number): void {
  confirmDial({
    variant: 'danger',
    title: langId === 1 ? 'CV үүсгээгүй байна' : 'You have not created a CV',
    message:
      langId === 1
        ? 'CV үүсгэсний дараа сонирхож буй ажлын байрны мэдээллээ засах боломжтой.'
        : 'After creating a CV, you can edit your target position information.',
    confirmText: langId === 1 ? 'Ок' : 'OK',
  });
}

export function requireCvAnket(
  isAnket: boolean,
  langId: number,
  onAllowed: () => void
): void {
  if (isAnket) {
    onAllowed();
    return;
  }

  showCvNotCreatedDialog(langId);
}
